import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppear } from '../hooks/useAppear';
import {
  IcoMapPin, IcoPhone, IcoMail, IcoClock,
  IcoWhatsApp, IcoHeart, IcoHandshake, IcoSend, IcoSpinner, IcoCheck
} from '../assets/icons';

const TYPE_VALS = ['general', 'partnership', 'volunteering', 'donation', 'press', 'other'];
const TYPE_PT   = ['Informação Geral', 'Parceria Institucional', 'Voluntariado', 'Apoio / Doação', 'Imprensa e Media', 'Outro'];
const TYPE_EN   = ['General Information', 'Institutional Partnership', 'Volunteering', 'Support / Donation', 'Press & Media', 'Other'];

export default function ContactPage() {
  const { i18n } = useTranslation();
  const isPt = i18n.language === 'pt';
  const ref  = useAppear();

  const [form, setForm] = useState({ name: '', email: '', phone: '', org: '', type: 'general', message: '' });
  const [sent, setSent]   = useState(false);
  const [loading, setLoading] = useState(false);

  const change = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const submit = (e) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => { setLoading(false); setSent(true); }, 1600);
  };

  const types = isPt ? TYPE_PT : TYPE_EN;

  const inputCls = 'w-full px-4 py-3 rounded-xl border border-brand-200 dark:border-brand-800 bg-white dark:bg-brand-950 text-brand-900 dark:text-white placeholder-brand-300 dark:placeholder-brand-700 focus:outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20 transition-all text-sm';

  return (
    <main>
      {/* Hero */}
      <section className="relative bg-brand-950 pt-[68px] overflow-hidden">
        <div className="absolute right-0 top-0 w-[600px] h-[600px] rounded-full bg-brand-600/8 blur-[120px] translate-x-1/2 -translate-y-1/3" />
        <div className="wrap relative z-10 py-24 text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-brand-800 bg-brand-900/60 mb-7">
            <span className="text-[11px] font-bold text-brand-500 uppercase tracking-[0.14em]">
              {isPt ? 'Estamos aqui para si' : 'We are here for you'}
            </span>
          </div>
          <h1 className="display-xl text-white mb-6">
            {isPt ? 'Fale Connosco' : 'Contact Us'}
          </h1>
          <p className="body-lg text-brand-400 max-w-xl mx-auto">
            {isPt
              ? 'Seja para informações, parcerias, voluntariado ou apoio responderemos com brevidade.'
              : 'Whether for information, partnerships, volunteering or support we will respond promptly.'}
          </p>
        </div>
      </section>

      {/* Form + info */}
      <section className="section-pad bg-white dark:bg-brand-950">
        <div className="wrap">
          <div className="grid lg:grid-cols-5 gap-14 items-start">

            {/* ── Form ── 3 cols */}
            <div className="lg:col-span-3">
              <h2 className="display-sm text-brand-900 dark:text-white mb-7">
                {isPt ? 'Enviar Mensagem' : 'Send a Message'}
              </h2>

              {sent ? (
                /* Success state */
                <div className="card-base p-12 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-brand-50 dark:bg-brand-900 border border-brand-100 dark:border-brand-800 flex items-center justify-center mx-auto mb-5">
                    <IcoCheck cls="w-8 h-8 text-brand-600" />
                  </div>
                  <h3 className="text-xl font-bold text-brand-900 dark:text-white mb-2">
                    {isPt ? 'Mensagem Enviada' : 'Message Sent'}
                  </h3>
                  <p className="text-brand-600 dark:text-brand-500 mb-7">
                    {isPt
                      ? 'Entraremos em contacto dentro de 48 horas úteis.'
                      : 'We will be in touch within 48 working hours.'}
                  </p>
                  <button onClick={() => setSent(false)} className="btn-outline text-sm py-2.5 px-6">
                    {isPt ? 'Enviar outra mensagem' : 'Send another message'}
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5">
                  {/* Contact type */}
                  <div>
                    <label className="block text-[12px] font-bold text-brand-600 uppercase tracking-[0.12em] mb-3">
                      {isPt ? 'Tipo de Contacto' : 'Contact Type'}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {types.map((t, i) => (
                        <button
                          type="button"
                          key={i}
                          onClick={() => setForm((f) => ({ ...f, type: TYPE_VALS[i] }))}
                          className={`px-3.5 py-1.5 rounded-full text-[12px] font-semibold transition-all border ${
                            form.type === TYPE_VALS[i]
                              ? 'bg-brand-600 border-brand-600 text-white'
                              : 'border-brand-200 dark:border-brand-800 text-brand-600 dark:text-brand-500 hover:border-brand-400'
                          }`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Fields */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    {[
                      { n: 'name',  lpt: 'Nome Completo',        len: 'Full Name',            t: 'text',  ppt: 'O seu nome completo',   pen: 'Your full name',           req: true  },
                      { n: 'email', lpt: 'Email',                 len: 'Email',                t: 'email', ppt: 'email@exemplo.com',     pen: 'email@example.com',        req: true  },
                      { n: 'phone', lpt: 'Telefone / WhatsApp',   len: 'Phone / WhatsApp',     t: 'tel',   ppt: '+244 9xx xxx xxx',       pen: '+244 9xx xxx xxx',         req: false },
                      { n: 'org',   lpt: 'Organização (opcional)',len: 'Organisation (opt.)',  t: 'text',  ppt: 'Nome da organização',    pen: 'Organisation name',        req: false },
                    ].map((f) => (
                      <div key={f.n}>
                        <label className="block text-[12px] font-semibold text-brand-700 dark:text-brand-400 mb-1.5">
                          {isPt ? f.lpt : f.len}{f.req ? ' *' : ''}
                        </label>
                        <input
                          type={f.t}
                          name={f.n}
                          required={f.req}
                          value={form[f.n]}
                          onChange={change}
                          placeholder={isPt ? f.ppt : f.pen}
                          className={inputCls}
                        />
                      </div>
                    ))}
                  </div>

                  <div>
                    <label className="block text-[12px] font-semibold text-brand-700 dark:text-brand-400 mb-1.5">
                      {isPt ? 'Mensagem *' : 'Message *'}
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      value={form.message}
                      onChange={change}
                      placeholder={isPt ? 'Escreva a sua mensagem, pedido ou proposta...' : 'Write your message, request or proposal...'}
                      className={`${inputCls} resize-none`}
                    />
                  </div>

                  <button type="submit" disabled={loading} className="btn-primary w-full py-4 text-[14px]">
                    {loading ? <IcoSpinner cls="w-5 h-5" /> : <IcoSend cls="w-5 h-5" />}
                    {loading
                      ? (isPt ? 'A enviar...' : 'Sending...')
                      : (isPt ? 'Enviar Mensagem' : 'Send Message')}
                  </button>
                </form>
              )}
            </div>

            {/* ── Info panel ── 2 cols */}
            <div ref={ref} className="lg:col-span-2 space-y-5">

              {/* Contact details */}
              <div className="card-base p-7 space-y-5">
                {[
                  { Icon: IcoMapPin, lpt: 'Sede',     len: 'HQ',     val: 'Luanda, Angola. Rua Rainha Ginga, Torre ElySee 8º Andar.',         href: null },
                  { Icon: IcoPhone,  lpt: 'Telefone', len: 'Phone',  val: '+244 925 700 016',       href: 'tel:+244925700016' },
                  { Icon: IcoMail,   lpt: 'Email',    len: 'Email',  val: 'geral@fdc.ao',   href: 'mailto:geral@fdc.ao' },
                  { Icon: IcoClock,  lpt: 'Horário',  len: 'Hours',  val: isPt ? 'Seg.–Sex. 08h–17h' : 'Mon–Fri 8am–5pm', href: null },
                ].map(({ Icon, lpt, len, val, href }, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-10 h-10 rounded-xl bg-brand-50 dark:bg-brand-900 border border-brand-100 dark:border-brand-800 flex items-center justify-center text-brand-600 flex-shrink-0">
                      <Icon cls="w-4.5 h-4.5 w-[18px] h-[18px]" />
                    </div>
                    <div>
                      <p className="text-[10px] text-brand-400 uppercase tracking-wider mb-0.5">
                        {isPt ? lpt : len}
                      </p>
                      {href
                        ? <a href={href} className="text-sm font-medium text-brand-800 dark:text-brand-200 hover:text-brand-600 dark:hover:text-brand-400 transition-colors">{val}</a>
                        : <p className="text-sm font-medium text-brand-800 dark:text-brand-200">{val}</p>
                      }
                    </div>
                  </div>
                ))}
              </div>

              {/* WhatsApp 
              <a
                href="https://wa.me/244923000000"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-5 rounded-xl bg-brand-600 hover:bg-brand-700 transition-colors group"
              >
                <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                  <IcoWhatsApp cls="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-bold text-white text-[14px]">
                    {isPt ? 'Falar via WhatsApp' : 'Chat via WhatsApp'}
                  </p>
                  <p className="text-brand-200 text-[12px]">+244 923 000 000</p>
                </div>
                <div className="ml-auto">
                  <svg viewBox="0 0 16 16" className="w-4 h-4 text-white/60 group-hover:text-white group-hover:translate-x-0.5 transition-all" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <line x1="3" y1="8" x2="13" y2="8"/>
                    <polyline points="9 4 13 8 9 12"/>
                  </svg>
                </div>
              </a>
*/}
              {/* Volunteer / Partner *
              {[
                {
                  Icon: IcoHeart,
                  title_pt: 'Voluntariado',
                  title_en: 'Volunteering',
                  desc_pt:  'Junte-se à nossa rede de voluntários e faça a diferença nas comunidades angolanas.',
                  desc_en:  'Join our volunteer network and make a difference in Angolan communities.',
                  mail:     'voluntariado@nganazenza.org',
                },
                {
                  Icon: IcoHandshake,
                  title_pt: 'Parcerias',
                  title_en: 'Partnerships',
                  desc_pt:  'A sua organização pode fazer parte desta missão. Conheça as possibilidades.',
                  desc_en:  'Your organisation can be part of this mission. Explore the possibilities.',
                  mail:     'parcerias@nganazenza.org',
                },
              ].map((c, i) => (
                <div key={i} className="card-orange-soft p-5 rounded-xl border">
                  <div className="flex gap-3 items-start">
                    <div className="w-10 h-10 rounded-xl bg-brand-100 dark:bg-brand-900 border border-brand-200 dark:border-brand-800 flex items-center justify-center text-brand-600 flex-shrink-0">
                      <c.Icon cls="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-brand-900 dark:text-white text-sm mb-1">
                        {isPt ? c.title_pt : c.title_en}
                      </h4>
                      <p className="text-[12px] text-brand-600 dark:text-brand-500 mb-2 leading-relaxed">
                        {isPt ? c.desc_pt : c.desc_en}
                      </p>
                      <a
                        href={`mailto:${c.mail}`}
                        className="text-[12px] font-semibold text-brand-600 hover:text-brand-700 dark:hover:text-brand-400 transition-colors underline underline-offset-2"
                      >
                        {c.mail}
                      </a>
                    </div>
                  </div>
                </div>
              ))}
              */}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
